// Durable background work (Phase 3.4). A "task" is a named unit of work that runs outside a request.
// Postgres holds the intent (what must run, for which subject, under which idempotency key). A runner
// (the in-process double in tests, a hosted scheduler later) only decides WHEN. The tuples are frozen:
// the database CHECK constraints of `background_job_intents` are generated from them, and
// `@lafayette/schemas` repeats the states (a parity test fails when one differs).
export const backgroundTaskIds = Object.freeze([
    "catalog-import.validate",
    "catalog-import.process",
    // Downstream intents of an imported item. They have no handler before CHT-709 and CHT-710: they
    // stay PENDING and are skipped by the dispatcher.
    "catalog-item.enrich",
    "catalog-item.embed",
    // Phase 5 (CHT-718 defines it, CHT-719 runs it): reads a message range of one conversation and proposes memory.
    "customer-memory.extract",
    // Milestone C (CHT-724, contract section 2 rule 7). `messaging.membership-sync` reconciles the Stream members of one
    // conversation with the computed set (subject: the conversation). `messaging.system-notice` posts one customer-safe notice
    // after a handoff event (subject: the handoff; the key names the event). Lane C writes the notice handler.
    "messaging.membership-sync",
    "messaging.system-notice",
    // CHT-724 review fix: ends the Stream access of a staff user whose messaging eligibility is gone (a deactivation, or the loss of
    // the concierge and supervisor roles): revokes the tokens and removes the user from every customer channel (subject: the staff
    // user). The staff use cases write it in their own transaction, so a committed change never loses its cleanup.
    "messaging.staff-access-revoke",
    // CHT-732: removes (hard delete) a staff message that was written into a customer channel by a staff user who did not own the
    // accepted handoff (Stream failed open on the before-send hook). Subject: the conversation. The key names the message.
    "messaging.staff-message-remove",
    // Milestone E, Phase 12 (CHT-761; docs/architecture/milestone-e-jobs-contract.md section 2.2). All the Phase 12 ids are published here at
    // once, so the lanes that implement them (request SLA, payment expiry and reconcile: CHT-763; notification delivery: CHT-764; trip
    // reminder and weather check: CHT-765) never edit this tuple or the database CHECK. A task with no handler stays PENDING.
    "request.sla-check",
    "payment-request.expire",
    "payment-request.reconcile",
    "notification.deliver",
    "trip.reminder",
    "itinerary-item.weather-check",
    // CHT-768 review fix: tells the push provider to forget ONE identity that a staff offboarding ended (subject: the staff user; the key names
    // the identity's provider id). The offboarding command ends the identity in its own transaction and writes this intent there, so a committed
    // offboarding never loses the cleanup. It acts on the ORIGINAL identity only: a later sign-in of the same person is another identity.
    "notification.identity-revoke",
    // CHT-771 (Phase 13.5): one privacy request, run by the API operations lane (it holds the Stream, push and Auth providers). Subject: the privacy
    // request. The run reads the request and its step ledger from Postgres, does only the steps that are not DONE, and records each one.
    "customer-privacy.export",
    "customer-privacy.delete",
    // CHT-780 review fix: a customer wrote while the AI is away (a pause, the cohort gate, the runtime off), so a person must be asked. The webhook,
    // or the reply executor, writes this intent in the SAME transaction as its decision; the task asks for the handoff and retries on its own.
    // Subject: the conversation. The key names the source message and the closed reason.
    "messaging.human-only-handoff",
    // CHT-764 (Expo push): checks the push RECEIPTS of one delivered outbox row, 15 minutes or more after the send. A ticket `ok` only says that
    // Expo accepted the message; the receipt says what Apple or Google did with it. A `DeviceNotRegistered` receipt ends that push token.
    // Subject: the outbox row (`notification_event`). The key names the row and the check number.
    "notification.receipt-check",
]);
export const backgroundSubjectTypes = Object.freeze([
    "import_job",
    "catalog_item",
    "memory_extraction_run",
    "conversation",
    "handoff",
    "staff_user",
    // Phase 12 (CHT-761).
    "concierge_request",
    "payment_request",
    "notification_event",
    "trip",
    "itinerary_item",
    // CHT-771.
    "privacy_request",
]);
/** `CLAIMED` holds a lease. `SUCCEEDED`, `FAILED` and `CANCELLED` are final. */
export const backgroundIntentStates = Object.freeze([
    "PENDING",
    "CLAIMED",
    "SUCCEEDED",
    "FAILED",
    "CANCELLED",
]);
export const backgroundIntentOpenStates = Object.freeze(["PENDING", "CLAIMED"]);
export const backgroundIntentFinalStates = Object.freeze([
    "SUCCEEDED",
    "FAILED",
    "CANCELLED",
]);
/** The subject type each task works on. */
export const backgroundTaskSubjects = Object.freeze({
    "catalog-import.validate": "import_job",
    "catalog-import.process": "import_job",
    "catalog-item.enrich": "catalog_item",
    "catalog-item.embed": "catalog_item",
    "customer-memory.extract": "memory_extraction_run",
    "messaging.membership-sync": "conversation",
    "messaging.system-notice": "handoff",
    "messaging.staff-access-revoke": "staff_user",
    "messaging.staff-message-remove": "conversation",
    "request.sla-check": "concierge_request",
    "payment-request.expire": "payment_request",
    "payment-request.reconcile": "payment_request",
    "notification.deliver": "notification_event",
    "trip.reminder": "trip",
    "itinerary-item.weather-check": "itinerary_item",
    "notification.identity-revoke": "staff_user",
    "customer-privacy.export": "privacy_request",
    "customer-privacy.delete": "privacy_request",
    "messaging.human-only-handoff": "conversation",
    "notification.receipt-check": "notification_event",
});
/**
 * The workload class of each task (contract section 4.2). The dispatcher claims class by class, in this order, so a large catalog
 * import cannot fill every claim of a tick while a payment or an SLA check waits. Messaging belongs to `interactive`.
 */
export const backgroundWorkloadClasses = Object.freeze([
    "critical",
    "interactive",
    "batch",
]);
export const backgroundTaskClasses = Object.freeze({
    "catalog-import.validate": "batch",
    "catalog-import.process": "batch",
    "catalog-item.enrich": "batch",
    "catalog-item.embed": "batch",
    "customer-memory.extract": "interactive",
    "messaging.membership-sync": "interactive",
    "messaging.system-notice": "interactive",
    "messaging.staff-access-revoke": "interactive",
    "messaging.staff-message-remove": "interactive",
    "request.sla-check": "critical",
    "payment-request.expire": "critical",
    "payment-request.reconcile": "critical",
    "notification.deliver": "critical",
    "trip.reminder": "batch",
    "itinerary-item.weather-check": "batch",
    "notification.identity-revoke": "interactive",
    "customer-privacy.export": "batch",
    "customer-privacy.delete": "interactive",
    "messaging.human-only-handoff": "critical",
    // The middle class: it is not a message to a person (critical), and it is not bulk work (batch).
    "notification.receipt-check": "interactive",
});
/**
 * Who ran the last attempt of an intent (contract section 5.2). `worker` is a Trigger.dev run, `api` is a lane of the API process,
 * `in-process` is the runner of tests and local development.
 */
export const backgroundRunners = Object.freeze(["worker", "api", "in-process"]);
/** Why an intent was cancelled (contract section 2.8). `null` for a cancel that gave no reason (the older callers). */
export const backgroundCancelReasons = Object.freeze([
    "SUBJECT_CANCELLED",
    "SUBJECT_DELETED",
    "SUPERSEDED",
    "OPERATOR",
]);
/**
 * What a run of a Phase 12 task decided (contract section 2.9). One row per run at most, with no free text. A lane adds a code only
 * through this tuple (an appended line).
 */
export const backgroundDecisionCodes = Object.freeze([
    "ACTED",
    "SUPERSEDED",
    "SUPPRESSED_RESOLVED",
    "SUPPRESSED_PREFERENCE",
    "SUPPRESSED_DUPLICATE",
    "SKIPPED_PROVIDER_UNAVAILABLE",
    "SKIPPED_PAUSED",
    "RETRY_EXHAUSTED",
    "NEEDS_RECONCILIATION",
]);
/** A reason or a policy version on a decision row: upper case code, or a short version string. No free text. */
export const BACKGROUND_REASON_CODE_PATTERN = /^[A-Z][A-Z0-9_]{0,63}$/;
export const BACKGROUND_POLICY_VERSION_PATTERN = /^[a-z0-9][a-z0-9.:_-]{0,63}$/;
/** Why an operator paused a task (contract section 4.1). */
export const backgroundControlReasons = Object.freeze([
    "INCIDENT",
    "PROVIDER_OUTAGE",
    "COST",
    "MAINTENANCE",
    "PROOF",
]);
/** The `task_id` of a control row that stops every task. */
export const BACKGROUND_CONTROL_ALL_TASKS = "*";
/** The classes of a task error (contract section 2.6). */
export const backgroundErrorClasses = Object.freeze([
    "RETRYABLE",
    "RATE_LIMITED",
    "AMBIGUOUS",
    "TERMINAL",
]);
/** The most attempts of each Phase 12 task family (contract section 2.6). Older families keep the defaults of their enqueue calls. */
export const BACKGROUND_PHASE_12_MAX_ATTEMPTS = Object.freeze({
    "request.sla-check": 5,
    "payment-request.expire": 8,
    "payment-request.reconcile": 8,
    "notification.deliver": 6,
    "notification.receipt-check": 4,
    "trip.reminder": 5,
    "itinerary-item.weather-check": 3,
    // CHT-771. A deletion has many steps and a provider step may fail for a while: it gets many attempts. An export is one step.
    "customer-privacy.delete": 12,
    "customer-privacy.export": 5,
});
/**
 * The meaning version of each task (CHT-761, contract section 2.5). The enqueue writes `current`: the repository does it when a caller gives
 * no version, so every producer of every family is covered. `runBackgroundTask` refuses an intent outside `[minSupported, current]` with
 * `TASK_VERSION_UNSUPPORTED` before the handler starts. Raise `current` when the meaning of a task changes; raise `minSupported` only when
 * old intents must not run any more.
 */
export const backgroundTaskVersions = Object.freeze(Object.fromEntries(backgroundTaskIds.map((taskId) => [taskId, Object.freeze({ current: 1, minSupported: 1 })])));
export const BACKGROUND_INTENT_MAX_ATTEMPTS = 20;
/** The longest idempotency key (visible ASCII). */
export const BACKGROUND_IDEMPOTENCY_KEY_MAX = 255;
